// Histórico de compras do cliente — ferramenta do VENDEDOR.
//
// Fica separado do "Consultar cliente" (crediário) de propósito: o vendedor não
// tem — nem deve ter — a permissão de ver dívida. O que ele precisa é saber o
// que o cliente já levou, quando e por quanto, pra puxar a próxima venda.
//
// Reusa a MESMA busca de cliente do crediário (sugestão pelo número da conversa
// + busca manual por nome/CPF), que o backend liberou pra esta permissão.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import backdropClose from '../helpers/backdropClose';
import { alertarErro } from '../helpers/alerts';

const dinheiro = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dia = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

function ListaCandidatos({ clientes, onEscolher }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {clientes.map((c) => (
        <button
          key={c.oid}
          type="button"
          onClick={() => onEscolher(c)}
          style={{
            textAlign: 'left', background: 'var(--portal-bg)', color: 'var(--portal-text)',
            border: '1px solid var(--portal-border)', borderRadius: 8,
            padding: '9px 12px', cursor: 'pointer',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
          }}
        >
          <span style={{ minWidth: 0 }}>
            <span style={{ fontWeight: 600, fontSize: '0.9rem', display: 'block' }}>{c.nome}</span>
            <span style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
              {c.cpf_cnpj || 'sem CPF/CNPJ'}
            </span>
          </span>
          <i className="bi bi-chevron-right" style={{ color: 'var(--portal-muted)', flexShrink: 0 }} />
        </button>
      ))}
    </div>
  );
}

// A lista de compras em si. `filtroProduto` responde "quando ele levou tal
// produto?" — busca DENTRO do que ele comprou, não no catálogo.
function ListaCompras({ oid }) {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');
  // Padrão 12 meses: cobre a conversa de venda quase sempre e é o mais leve.
  // Quem precisa de mais fundo troca aqui — o custo é da largura do período.
  const [periodo, setPeriodo] = useState('12m');

  // Carrega TUDO do período uma vez. O filtro por produto é feito aqui no
  // cliente, na hora que digita — instantâneo, sem nova ida ao ERP.
  useEffect(() => {
    let vivo = true;
    setCarregando(true);
    axios.get(`${API}/consulta-cliente/${oid}/compras`, { params: { periodo } })
      .then((r) => { if (vivo) setDados(r.data?.data || { compras: [] }); })
      .catch(() => { if (vivo) setDados({ compras: [], erro: true }); })
      .finally(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
  }, [oid, periodo]);

  const termo = busca.trim().toLowerCase();
  const filtro = termo; // pra reaproveitar as mensagens de "sem resultado"
  const todas = dados?.compras || [];
  // Mostra a compra que TEM o produto, só com os itens que casam, e o total
  // recalculado — igual o backend fazia, mas na hora.
  const compras = !termo ? todas : todas
    .map((c) => {
      const itens = c.itens.filter((it) => `${it.codpro} ${it.descricao}`.toLowerCase().includes(termo));
      if (!itens.length) return null;
      return { ...c, itens, total: itens.reduce((soma, it) => soma + Number(it.total_pago || 0), 0) };
    })
    .filter(Boolean);

  const PERIODOS = [
    { chave: '12m', rotulo: '12 meses' },
    { chave: '24m', rotulo: '2 anos' },
    { chave: 'tudo', rotulo: 'desde sempre' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <input
            className="form-control form-control-sm"
            placeholder="Filtrar por produto — filtra enquanto você digita"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          {termo && (
            <button
              type="button"
              onClick={() => setBusca('')}
              title="Limpar filtro"
              style={{
                position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                background: 'transparent', border: 'none', color: 'var(--portal-muted)',
                cursor: 'pointer', fontSize: '0.9rem', lineHeight: 1,
              }}
            >
              <i className="bi bi-x-circle" />
            </button>
          )}
        </div>
        {termo && !carregando && (
          <span style={{ fontSize: '0.75rem', color: 'var(--portal-muted)', whiteSpace: 'nowrap' }}>
            {compras.length} de {todas.length}
          </span>
        )}
      </div>

      {/* Até onde puxar. Fica sempre visível, inclusive enquanto carrega, pra
          ele ver que a troca de período foi registrada. */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.75rem', color: 'var(--portal-muted)' }}>Período:</span>
        {PERIODOS.map((p) => {
          const ativo = p.chave === periodo;
          return (
            <button
              key={p.chave}
              type="button"
              onClick={() => setPeriodo(p.chave)}
              disabled={carregando}
              style={{
                background: ativo ? 'rgba(61,139,55,0.16)' : 'transparent',
                color: ativo ? '#3D8B37' : 'var(--portal-muted)',
                border: `1px solid ${ativo ? 'rgba(61,139,55,0.45)' : 'var(--portal-border)'}`,
                borderRadius: 20, padding: '2px 10px', fontSize: '0.75rem',
                fontWeight: ativo ? 600 : 400,
                cursor: carregando ? 'wait' : 'pointer',
              }}
            >
              {p.rotulo}
            </button>
          );
        })}
      </div>

      {carregando && (
        <p style={{ color: 'var(--portal-muted)', fontSize: '0.85rem' }}>Buscando as compras…</p>
      )}

      {/* Cliente grande (construtora, revenda) pode ter milhares de compras.
          Aparecem as mais recentes — dizer isso evita ele concluir que o resto
          não existe. Pra achar coisa antiga, o filtro por produto é o caminho. */}
      {!carregando && dados?.total_compras > todas.length && (
        <div style={{ fontSize: '0.75rem', color: 'var(--portal-muted)' }}>
          Mostrando as {todas.length} compras mais recentes, de{' '}
          {Number(dados.total_compras).toLocaleString('pt-BR')} no período.
        </div>
      )}

      {!carregando && compras.length === 0 && (
        <p style={{ color: 'var(--portal-muted)', fontSize: '0.85rem' }}>
          {filtro
            ? 'Esse produto não aparece nas compras dele nesse período.'
            : 'Nenhuma compra nesse período. Venda de balcão sem pedido não fica no nome do cliente.'}
        </p>
      )}

      {!carregando && compras.map((c) => (
        <div key={c.documento} style={{
          border: '1px solid var(--portal-border)', borderRadius: 8, padding: '9px 11px',
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', gap: 8,
            flexWrap: 'wrap', marginBottom: 6, fontSize: '0.8rem',
          }}>
            <span>
              <strong>{dia(c.data)}</strong>
              <span style={{ color: 'var(--portal-muted)' }}>
                {' · '}{c.documento}{' · '}filial {c.filial}{c.vendedor ? ` · ${c.vendedor}` : ''}
              </span>
            </span>
            <strong>{dinheiro(c.total)}</strong>
          </div>
          {c.itens.map((it, i) => (
            <div key={`${c.documento}-${it.codpro}-${i}`} style={{
              display: 'flex', justifyContent: 'space-between', gap: 10,
              fontSize: '0.8rem', padding: '2px 0',
            }}>
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {/* Código ANTES da descrição de propósito: a linha corta com
                    reticências no fim, e no fim o código sumiria justo nos nomes
                    longos. É por ele que se acha o produto no ERP. */}
                <span style={{ color: 'var(--portal-muted)' }}>{it.qtd}x </span>
                <span style={{ color: 'var(--portal-muted)' }}>{it.codpro} </span>
                {it.descricao}
              </span>
              <span style={{ whiteSpace: 'nowrap' }}>
                {/* Teve desconto? O de tabela riscado ao lado do pago — é onde a
                    negociação aparece, que é pra isso que ele abre esta tela. */}
                {it.total_cheio > it.total_pago && (
                  <span style={{
                    textDecoration: 'line-through', color: 'var(--portal-muted)',
                    marginRight: 5, fontSize: '0.75rem',
                  }}>
                    {dinheiro(it.qtd ? it.total_cheio / it.qtd : it.total_cheio)}
                  </span>
                )}
                {dinheiro(it.unitario_pago)}
                {it.unidade ? <span style={{ color: 'var(--portal-muted)' }}>/{it.unidade}</span> : null}
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export default function HistoricoClienteModal({ aberto, numero, nomeContato, onClose }) {
  const [candidatos, setCandidatos] = useState([]);
  const [buscaFeita, setBuscaFeita] = useState(false);
  const [termo, setTermo] = useState('');
  const [resultados, setResultados] = useState(null); // busca manual: null = não buscou
  const [buscando, setBuscando] = useState(false);
  const [cliente, setCliente] = useState(null); // { oid, nome }

  // Ao abrir, tenta adivinhar o cliente pelo número da conversa (mesmo caminho
  // do crediário). O número nem sempre bate — daí a busca manual.
  useEffect(() => {
    if (!aberto) return;
    setCliente(null); setResultados(null); setTermo(''); setBuscaFeita(false);
    if (!numero) { setCandidatos([]); setBuscaFeita(true); return; }
    axios.get(`${API}/consulta-cliente/sugestao`, { params: { numero } })
      .then((r) => setCandidatos(r.data?.data?.candidatos || []))
      .catch(() => setCandidatos([]))
      .finally(() => setBuscaFeita(true));
  }, [aberto, numero]);

  if (!aberto) return null;

  const buscar = async (e) => {
    e?.preventDefault();
    const t = termo.trim();
    if (t.length < 3) return;
    setBuscando(true);
    try {
      const r = await axios.get(`${API}/consulta-cliente/buscar`, { params: { q: t } });
      // O backend devolve { clientes: [...], ampla }, não um array direto.
      setResultados(r.data?.data?.clientes || []);
    } catch (err) {
      alertarErro(err, 'Buscar cliente');
    } finally { setBuscando(false); }
  };

  return (
    <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.72)' }} {...backdropClose(onClose)}>
      <div
        className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
        style={{ maxWidth: 720 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content" style={{ background: 'var(--portal-surface)', color: 'var(--portal-text)' }}>
          <div className="modal-header" style={{ borderColor: 'var(--portal-border)', paddingBottom: 10 }}>
            <div style={{ minWidth: 0 }}>
              <h5 className="modal-title" style={{ fontSize: '1rem', marginBottom: 2 }}>
                <i className="bi bi-clock-history me-2" />
                {cliente ? cliente.nome : 'Últimas compras do cliente'}
              </h5>
              <div style={{ fontSize: '0.75rem', color: 'var(--portal-muted)' }}>
                Microuniverso · somente consulta
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              {cliente && (
                <button
                  type="button"
                  onClick={() => setCliente(null)}
                  style={{
                    background: 'transparent', border: '1px solid var(--portal-border)',
                    color: 'var(--portal-text)', borderRadius: 6, padding: '0 10px',
                    fontSize: '0.8rem', cursor: 'pointer', whiteSpace: 'nowrap',
                  }}
                >
                  trocar cliente
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                aria-label="Fechar"
                title="Fechar"
                style={{
                  background: 'transparent', border: '1px solid var(--portal-border)',
                  color: 'var(--portal-text)', borderRadius: 6, width: 32, height: 32,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', flexShrink: 0, fontSize: '1rem', lineHeight: 1,
                }}
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
          </div>

          <div className="modal-body">
            {cliente ? (
              <ListaCompras oid={cliente.oid} />
            ) : (
              <>
                {/* Palpite pelo número, quando houver */}
                {candidatos.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: '0.78rem', color: 'var(--portal-muted)', marginBottom: 6 }}>
                      {nomeContato ? `Pelo número de ${nomeContato}:` : 'Pelo número da conversa:'}
                    </div>
                    <ListaCandidatos clientes={candidatos} onEscolher={setCliente} />
                  </div>
                )}

                <form onSubmit={buscar} style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                  <input
                    className="form-control form-control-sm"
                    placeholder="Buscar por nome ou CPF/CNPJ"
                    value={termo}
                    onChange={(e) => setTermo(e.target.value)}
                    autoFocus={candidatos.length === 0}
                  />
                  <button type="submit" className="btn btn-sm"
                    disabled={buscando || termo.trim().length < 3}
                    style={{ background: '#3D8B37', color: '#fff', border: 'none', whiteSpace: 'nowrap' }}>
                    {buscando ? '...' : 'Buscar'}
                  </button>
                </form>

                {resultados !== null && (
                  resultados.length > 0 ? (
                    <ListaCandidatos clientes={resultados} onEscolher={setCliente} />
                  ) : (
                    <p style={{ color: 'var(--portal-muted)', fontSize: '0.85rem' }}>
                      Nenhum cliente com esse nome ou CPF/CNPJ.
                    </p>
                  )
                )}

                {resultados === null && candidatos.length === 0 && buscaFeita && (
                  <p style={{ color: 'var(--portal-muted)', fontSize: '0.85rem' }}>
                    O número da conversa não achou cliente no ERP. Busque pelo nome ou CPF/CNPJ.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
